import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Clock, Tag, ArrowRight, BookOpen, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15 } })
};

const guides = [
  { title: '7-Step Manifestation Formula', desc: 'A practical guide to manifesting any goal in your life.', category: 'Guide' },
  { title: 'Morning Abundance Meditation', desc: '15-minute guided meditation for wealth consciousness.', category: 'Meditation' },
  { title: 'Subconscious Mind Cheat Sheet', desc: 'Quick reference for reprogramming techniques.', category: 'Cheat Sheet' },
];

export default function ResourcesPage() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => setPosts(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-indigo-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase text-indigo-600 font-semibold">Free Resources</span>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.1] text-slate-900 mt-4 mb-6">
              Learn & <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600">Grow</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Explore articles, videos, and free downloadable guides to kickstart your transformation journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-serif text-3xl sm:text-4xl text-slate-900 mb-12">
            Articles & Insights
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {posts.map((post, i) => (
              <motion.div key={post.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col" data-testid={`blog-card-${post.id}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-indigo-50 text-indigo-600">{post.category}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{post.read_time}</span>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">{post.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">{post.excerpt}</p>
                    <Button variant="ghost" className="self-start text-indigo-600 hover:text-indigo-700 gap-2 px-0" data-testid={`read-more-${post.id}`}>
                      Read More <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Section */}
      <section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mb-4">Watch & Learn</h2>
            <p className="text-slate-600">Subscribe to our YouTube channel for weekly transformation tips.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'How to Manifest Anything in 7 Steps', id: 'kC2iyXPyud8' },
              { title: 'Break Free from Money Blocks', id: 'fVXpo12-AsA' },
              { title: 'Morning Meditation for Abundance', id: 'AnhobWW_DQs' },
            ].map((video, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                <div className="rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-all" data-testid={`youtube-video-${i}`}>
                  <div className="relative aspect-video bg-slate-200">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-medium text-slate-900 text-sm">{video.title}</h4>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Guides */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-serif text-3xl sm:text-4xl text-slate-900 mb-12">
            Free Downloadable Guides
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {guides.map((guide, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                <div className="bg-gradient-to-br from-indigo-50 to-amber-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-all" data-testid={`guide-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm">
                    <BookOpen className="w-6 h-6 text-indigo-600" />
                  </div>
                  <span className="text-xs font-medium text-indigo-600 mb-2 block">{guide.category}</span>
                  <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">{guide.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{guide.desc}</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-sm gap-2" data-testid={`download-guide-${i}`}>
                    Download Free <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
